import { Button, Dialog, Divider, FormControlLabel, Switch, TextField } from "@mui/material";
import axios from "axios";
import { useState, useEffect } from "react";
import { ProjectConfig } from 'Global';
import { useSnackbar } from "notistack";

interface CompanyNumerationDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  company_id: number | null;
  current_order_id: string;
  client_code_consecutive_enabled: boolean;
  client_code_consecutive_value: string;
  loadList: () => void;
  setLoading: (loading: boolean) => void;
}

export default function CompanyNumerationDialog({
  open,
  setOpen,
  company_id,
  current_order_id,
  client_code_consecutive_enabled,
  client_code_consecutive_value,
  loadList,
  setLoading
}: CompanyNumerationDialogProps) {

  const { enqueueSnackbar } = useSnackbar();
  const [orderIdValue, setOrderIdValue] = useState("");
  const [consecutiveEnabled, setConsecutiveEnabled] = useState(false);
  const [consecutiveValue, setConsecutiveValue] = useState("");

  useEffect(() => {
    if (open) {
      setOrderIdValue(current_order_id || "0");
      setConsecutiveEnabled(client_code_consecutive_enabled || false);
      setConsecutiveValue(client_code_consecutive_value || "");
    }
  }, [open, current_order_id, client_code_consecutive_enabled, client_code_consecutive_value]);

  const handleUpdateOrders = () => {
    if (!company_id || !orderIdValue) {
      enqueueSnackbar('El ID de la empresa y el ID del pedido son requeridos', { variant: 'error' });
      return;
    }

    setLoading(true);
    axios.post(ProjectConfig.api_url + "/companies/updateCompanyOrderId", {
      company_id,
      current_order_id: orderIdValue
    })
      .then((response) => {
        if (response.data.status === "success") {
          enqueueSnackbar('Numeración de pedidos actualizada con éxito', { variant: 'success' });
          loadList();
        } else {
          enqueueSnackbar(response.data.message, { variant: 'error' });
        }
      })
      .catch(() => enqueueSnackbar('Error al actualizar numeración de pedidos', { variant: 'error' }))
      .finally(() => setLoading(false));
  };

  const handleUpdateClients = () => {
    if (!company_id) {
      enqueueSnackbar('El ID de la empresa es requerido', { variant: 'error' });
      return;
    }
    if (consecutiveEnabled && !consecutiveValue) {
      enqueueSnackbar('El consecutivo de código de cliente es requerido', { variant: 'error' });
      return;
    }

    setLoading(true);
    axios.post(ProjectConfig.api_url + "/companies/updateCompanyNumeration", {
      company_id,
      client_code_consecutive_enabled: consecutiveEnabled,
      client_code_consecutive_value: consecutiveValue
    })
      .then((response) => {
        if (response.data.status === "success") {
          enqueueSnackbar('Numeración de clientes actualizada con éxito', { variant: 'success' });
          loadList();
        } else {
          enqueueSnackbar(response.data.message, { variant: 'error' });
        }
      })
      .catch(() => enqueueSnackbar('Error al actualizar numeración de clientes', { variant: 'error' }))
      .finally(() => setLoading(false));
  };

  const handleClose = () => {
    setOpen(false);
    setOrderIdValue("");
    setConsecutiveEnabled(false);
    setConsecutiveValue("");
  };

  return (
    <Dialog 
      open={open}
      onClose={handleClose}
      aria-labelledby="numeration-dialog-title"
      aria-describedby="numeration-dialog-description"
    >
      <div className="DialogContainer" style={{maxWidth:'400px'}}>
        <div className="ModuleWrapper">
          <div className="boxContainerHeader">
            <div className="boxContainerTitle">
              <h2 className="h2_title">Numeración de Empresa</h2>
              <span className="h2_subtitle">Gestionar numeraciones</span>
            </div>
          </div>

          <div id="numerationForm">
            {/* SECCIÓN PEDIDOS */}
            <div className="input_group">
              <h3 style={{margin: '0 0 10px 0'}}>Pedidos</h3>
              <div className="input_row">
                <TextField
                  id="current_order_id"
                  name="current_order_id"
                  label="ID de Pedido Actual"
                  type="text"
                  fullWidth
                  value={orderIdValue}
                  onChange={(e) => setOrderIdValue(e.target.value)}
                  placeholder="Ingrese nuevo ID de pedido"
                />
              </div>
              <div className="input_row" style={{display:'flex', justifyContent:'flex-end'}}>
                <Button
                  onClick={handleUpdateOrders}
                  variant="contained"
                  className="button_1"
                  size="small"
                >
                  Actualizar Pedidos
                </Button>
              </div>
            </div>

            <Divider sx={{my: 2}} />

            {/* SECCIÓN CLIENTES */}
            <div className="input_group">
              <h3 style={{margin: '0 0 10px 0'}}>Clientes</h3>
              <div className="input_row">
                <FormControlLabel
                  control={
                    <Switch
                      checked={consecutiveEnabled}
                      onChange={(e) => setConsecutiveEnabled(e.target.checked)}
                    />
                  }
                  label="Asignar consecutivo a código de cliente"
                />
              </div>

              {consecutiveEnabled && (
                <div className="input_row">
                  <TextField
                    id="client_code_consecutive"
                    name="client_code_consecutive"
                    label="Consecutivo de Código de Cliente"
                    type="text"
                    fullWidth
                    required
                    value={consecutiveValue}
                    onChange={(e) => setConsecutiveValue(e.target.value)}
                    placeholder="Ingrese el consecutivo"
                  />
                </div>
              )}
              <div className="input_row" style={{display:'flex', justifyContent:'flex-end'}}>
                <Button
                  onClick={handleUpdateClients}
                  variant="contained"
                  className="button_1"
                  size="small"
                >
                  Actualizar Clientes
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="dialogButtons">
        <Button
          onClick={handleClose}
          variant="outlined"
          className="button_2"
        >
          Cancelar
        </Button>
      </div>
    </Dialog>
  );
}
